import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ymd } from '@/utils/agendaItem'

/**
 * 뷰 상태를 URL 쿼리 하나로 관리한다.
 *
 * 규칙: **경로는 화면을 지칭하고, 뷰 모드·필터·선택·열린 시트는 전부 쿼리다.**
 *
 * 왜 이렇게 하나:
 *   기존 Calendar.vue 는 path param, ?id, ?mine, ?date, ?agenda, myEventsOnly,
 *   selectedAgendaDate 각각에 양방향 watcher 를 달아 7개를 돌리고 있었다.
 *   여러 watcher 가 loadEvents() 를 부르니 중복 fetch 가드까지 붙었다.
 *   쿼리를 단일 진실로 두면 watcher 는 하나(route.query)로 끝나고,
 *   모든 사용자 액션은 patch() 로 URL 을 바꾸기만 한다. 루프가 생길 여지가 없다.
 *
 * 부수 효과로 얻는 것:
 *   - 새로고침/공유 링크가 화면 상태를 그대로 복원한다
 *   - 뒤로가기가 자연스럽게 동작한다
 *   - keep-alive 로 캐시된 컴포넌트도 쿼리 변경에 반응한다
 */

const AGENDA_VIEWS = ['month', 'week', 'day', 'agenda']
// 서버 resolveScope(middleware/agendaScope.js)가 아는 값만. 'unassigned' 는 서버가 모르는 값이라
// 'all' 로 조용히 바뀌었고 ScopeControl 에서도 이미 뺐다.
const SCOPES = ['mine', 'department', 'user', 'all']

export function useAgendaQuery(defaults = {}) {
  const route = useRoute()
  const router = useRouter()

  const state = computed(() => {
    const q = route.query
    return {
      // 쿼리가 없으면 경로(/calendar/:view)를 본다. 라우터가 /calendar/week 를 받으면서도
      // 그 값을 아무도 읽지 않아, 공유된 주 뷰 링크가 월 뷰로 열렸다.
      view: AGENDA_VIEWS.includes(q.view)
        ? q.view
        : AGENDA_VIEWS.includes(route.params.view) ? route.params.view : defaults.view || 'month',
      date: /^\d{4}-\d{2}-\d{2}$/.test(q.date || '') ? q.date : ymd(new Date()),
      scope: SCOPES.includes(q.scope) ? q.scope : defaults.scope || 'all',
      userId: q.userId ? Number(q.userId) : null,
      deptId: q.deptId ? Number(q.deptId) : null,
      kinds: (q.kinds || defaults.kinds || 'schedule,task').split(',').filter(Boolean),
      q: q.q || '',
      // 열린 시트도 URL 에 둔다 → 안드로이드 뒤로가기가 시트를 닫는다
      sheet: q.sheet || '',
      sheetId: q.sheetId || ''
    }
  })

  /**
   * 쿼리 부분 갱신. 빈 값은 URL 에서 제거해 주소를 짧게 유지한다.
   * @param {object} partial
   * @param {{push?: boolean}} opts push=true 면 히스토리에 쌓인다(뒤로가기 대상)
   */
  function patch(partial, { push = false } = {}) {
    const next = { ...route.query, ...partial }
    for (const k of Object.keys(next)) {
      const v = next[k]
      if (v == null || v === '' || (Array.isArray(v) && !v.length)) delete next[k]
    }
    const nav = push ? router.push : router.replace
    // 같은 위치로의 이동은 vue-router 가 거부하며 reject 한다. 무시해도 되는 오류다.
    nav.call(router, { query: next }).catch(() => {})
  }

  /** 시트 열기 — push 라서 뒤로가기로 닫힌다 */
  function openSheet(name, id) {
    patch({ sheet: name, sheetId: id != null ? String(id) : '' }, { push: true })
  }

  /** 시트 닫기 — 히스토리에 쌓인 경우 back(), 아니면 쿼리만 제거 */
  function closeSheet() {
    if (route.query.sheet) {
      const canGoBack = window.history.state && window.history.state.back
      if (canGoBack) router.back()
      else patch({ sheet: '', sheetId: '' })
    }
  }

  return { state, patch, openSheet, closeSheet }
}
